import { Stack, useTheme } from "@mui/material";
import { PartsList, ConfigureProductSection, ProductsListSection } from "./components";
import { AddEditConfigurationDialog } from "../configurator/components";
import { RootState } from "src/store";
import { connect } from "react-redux";
import { Button, TooltipShortcut } from "@itk/mui-components";
import { genericTranslationsK, useGenericTranslationsT } from "src/translation-hooks/generic-translations";
import { configuratorPageK, useConfiguratorPageT } from "src/translation-hooks/configurator-page";
import { useNavigate, useParams } from "react-router";
import { applicationActions } from "src/components/application-store";
import { configuratorActions } from "../configurator/configurator-store";
import { useEffect } from "react";
import { DekoxIcon } from "src/components";
import { configureProductActions } from "./configure-product-store";
import { CONSTANTS, ROUTES_URL } from "src/utils/constants";
import sidebarBackground from "src/assets/images/sidebar-background.svg";

const mapStateToProps = (state: RootState) => ({
  addEditConfigurationDialog: state.pageReducer.combinedConfiguratorReducer.addEditConfigurationDialogReducer.addEditConfigurationDialog,
  configurationState: state.pageReducer.configureProductReducer.configurationState
});

const ownActions = {
  setWarningAction: applicationActions.setWarningAction,
  deleteConfigurationsRequest: configuratorActions.deleteConfigurationsRequest,
  getConfigurationRequest: configureProductActions.getConfigurationRequest,
  getGenerateConfigurationOrderRequest: configureProductActions.getGenerateConfigurationOrderRequest,
  clear: configureProductActions.clear,
  getLabelsRequest: configuratorActions.getLabelsRequest
};

type Props = typeof ownActions & ReturnType<typeof mapStateToProps>;

const ConfigureProductBase = (props: Props) => {
  const { addEditConfigurationDialog, deleteConfigurationsRequest, setWarningAction, getConfigurationRequest, configurationState, clear, getLabelsRequest, getGenerateConfigurationOrderRequest } =
    props;

  const theme = useTheme();
  const navigate = useNavigate();

  const { configurationId = "", projectId = "" } = useParams<{ configurationId: string; projectId: string }>();

  const genericTranslationsT = useGenericTranslationsT();
  const configuratorPageT = useConfiguratorPageT();

  // Clear redux state on unmount
  useEffect(() => {
    return () => {
      clear();
    };
  }, []);

  // get configuration
  useEffect(() => {
    if (configurationId) {
      getConfigurationRequest(configurationId);
      getLabelsRequest(projectId);
      getGenerateConfigurationOrderRequest(projectId, configurationId);
    }
  }, [configurationId]);

  const handleClose = () => {
    clear();
    navigate(-1);
  };

  const handleDelete = () => {
    setWarningAction({
      confirm: () => {
        if (!configurationId) return;
        deleteConfigurationsRequest({ configurationIds: [configurationId] }, isSuccess => {
          if (isSuccess) {
            navigate(ROUTES_URL.CONFIGURATOR.build(projectId));
          }
        });
        setWarningAction(undefined);
      },
      dialogProps: {
        message: configuratorPageT.areYouSureYouWantToDeleteThisProductConfiguration,
        confirmButtonTranslation: {
          t: genericTranslationsT,
          k: genericTranslationsK,
          field: "delete"
        },
        messageIcon: <DekoxIcon icon="delete" />
      },
      variant: "confirmCancel"
    });
  };

  return (
    <>
      <Stack direction="row" flex={1} overflow="hidden">
        {/* Left: products list — full height, no footer */}
        <Stack
          width={CONSTANTS.DRAWER_WIDTH}
          padding={4}
          overflow="auto"
          alignSelf="stretch"
          sx={{ backgroundImage: `url(${sidebarBackground})`, backgroundRepeat: "no-repeat", backgroundSize: "cover", backgroundPosition: "bottom" }}
        >
          <ProductsListSection />
        </Stack>

        {/* Right: configure + parts on top, footer on bottom */}
        <Stack flex={1} overflow="hidden">
          <Stack direction="row" flex={1} overflow="hidden">
            {/* Product configuration */}
            <Stack bgcolor={theme.palette.customColors.white} margin={4} marginBottom={4} width={300} overflow="hidden">
              <ConfigureProductSection />
            </Stack>
            {/* 3d modal */}
            <Stack overflow="auto" px={3} py={4} borderRadius={1} border={`1px solid ${theme.palette.customColors.grey}`} bgcolor={theme.palette.customColors.lightGrey2} flex={1}>
              <PartsList />
            </Stack>
          </Stack>
          <Stack px={4} py={2} direction="row" alignItems="center" justifyContent="space-between" bgcolor={theme.palette.customColors.white} borderTop={`1px solid ${theme.palette.customColors.grey}`}>
            <TooltipShortcut shortcut={{ action: handleDelete, shortCutKey: "s", modifier: "alt" }}>
              <Button
                disabled={configurationState.loading === "is-loading"}
                variant="text"
                color="error"
                sx={{ textDecoration: "underline" }}
                t={configuratorPageT}
                k={configuratorPageK}
                field="deleteConfiguration"
                onClick={handleDelete}
              />
            </TooltipShortcut>
            <TooltipShortcut shortcut={{ action: handleClose, shortCutKey: "g", modifier: "alt" }}>
              <Button variant="contained" t={genericTranslationsT} k={genericTranslationsK} field="saveAndClose" onClick={handleClose} />
            </TooltipShortcut>
          </Stack>
        </Stack>
      </Stack>
      {addEditConfigurationDialog && <AddEditConfigurationDialog />}
    </>
  );
};

export const ConfigureProduct = connect(mapStateToProps, ownActions)(ConfigureProductBase);
